/* eslint-disable react/no-array-index-key */
import React from 'react';
import i18n from 'i18next';
import copy from 'copy-to-clipboard';
import FacebookRounded from '@mui/icons-material/FacebookRounded';
import LinkedIn from '@mui/icons-material/LinkedIn';
import Print from '@mui/icons-material/Print';
import Share from '@mui/icons-material/Share';
import Twitter from '@mui/icons-material/Twitter';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';

import { Button, ButtonGroup } from 'uikit/buttons';
import { Icon } from 'uikit/icons';
import { Loading } from 'uikit/loaders';
import { Menu, MenuItem } from 'uikit/menus';
import { Tooltip } from 'uikit/tooltips';
import { Typo } from 'uikit/content';
import { addToast } from 'core/notifications';
import getDateString from '../helpers/getDateString';

import styles from '../styles/credentialModal.module.scss';

const CredentialModal = ({
  credential,
  reputationEarned,
  userCredential,
  gamification,
  goldEarned,
  isLoading,
  isShareable,
}) => {
  if (isLoading) {
    return (
      <div className={styles.loader}>
        <Loading />
      </div>
    );
  }

  const credentialViewURL = window.location.origin + userCredential.certification_print_path;

  const addToLinkedinURL = () => {
    const params = new URLSearchParams({
      startTask: 'CERTIFICATION_NAME',
      name: credential.name,
      issueYear: new Date(userCredential.created_at).toLocaleDateString(
        document.documentElement.lang,
        { year: 'numeric', timeZone: 'UTC' },
      ),
      issueMonth: new Date(userCredential.created_at).toLocaleDateString(
        document.documentElement.lang,
        { month: '2-digit', timeZone: 'UTC' },
      ),
      expirationYear: new Date(userCredential.expries_at).toLocaleDateString(
        document.documentElement.lang,
        { year: 'numeric', timeZone: 'UTC' },
      ),
      expirationMonth: new Date(userCredential.expries_at).toLocaleDateString(
        document.documentElement.lang,
        { month: '2-digit', timeZone: 'UTC' },
      ),
      certUrl: credentialViewURL,
    }).toString();

    return `https://www.linkedin.com/profile/add?${params}`;
  };

  // This will have to change once everything is moved over to React
  const handlePrintClick = () => {
    const newWindow = window.open(credentialViewURL);
    newWindow.focus();
    newWindow.addEventListener('load', () => {
      newWindow.print();
    });
  };

  const handleShareClick = (url, target, width = 600, height = 500) => {
    window.open(
      url,
      `share-${target}-window`,
      `left=20, top=20, width=${width}, height=${height}, toolbar=0, resizable=1`,
    );
  };

  const handleCopyClick = () => {
    copy(credentialViewURL);
    addToast('Copied URL', 'success');
  };

  return (
    <div className={styles.element}>
      <div className={styles.header}>
        <div className={styles.subject}>
          <Typo element="h2" variant="d03">
            {i18n.t('credential-earned')}
          </Typo>
        </div>
        <div className={styles.image}>
          {userCredential.custom_svg ? (
            <div
              className={styles.customSVG}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: userCredential.custom_svg }}
            />
          ) : (
            <img
              className={styles.imageSrc}
              src={credential.badge_absolute_url}
              alt={`${credential.name}`}
            />
          )}
        </div>
        <div className={styles.title}>
          <Typo element="h3" variant="d07">
            {credential.name}
          </Typo>
        </div>
        <div className={styles.byline}>
          <Typo element="span" variant="b03" weight="medium">
            {i18n.t('credential-completed-by-name-on-date', {
              name: userCredential.user_full_name,
              date: getDateString(userCredential.created_at),
            })}
          </Typo>
        </div>
      </div>
      <div className={styles.body}>
        {gamification.is_reputation_awarded && !!reputationEarned?.length && (
          <div className={styles.reputation}>
            <div className={styles.reputationTitle}>
              <Icon className={styles.reputationIcon} icon="reputation" size={16} />
              <Typo element="span" variant="b02" weight="medium">
                {i18n.t('reputation-earned-in', {
                  points: userCredential.reputation_points,
                })}
              </Typo>
            </div>
            <div className={styles.skills}>
              {reputationEarned.map((skill, index) => (
                <a
                  className={styles.skillsItem}
                  href={`/student/catalog/list?inline_search=true&taxonomy_tag_ids[]=${skill.id}`}
                  key={index}
                >
                  <span className={styles.dot} style={{ backgroundColor: skill.hex_color }} />
                  <Typo element="span" variant="s01">
                    {skill.name}
                  </Typo>
                </a>
              ))}
            </div>
          </div>
        )}
        <div className={styles.description}>
          <Typo variant="b02">{credential.description}</Typo>
        </div>
        <div className={styles.meta}>
          {!!userCredential.score && (
            <div className={styles.metaItem}>
              <Typo element="span" variant="b01">
                {`${i18n.t('score')}: ${userCredential.score}`}
              </Typo>
            </div>
          )}
          <div className={styles.metaItem}>
            <Typo element="span" variant="b01">
              {`${i18n.t('completion-id')}: ${userCredential.completion_id}`}
            </Typo>
          </div>
          {userCredential.expires_at && (
            <div className={styles.metaItem}>
              <Typo element="span" variant="b01">
                {`${i18n.t('expires')}: ${getDateString(userCredential.expires_at)}`}
              </Typo>
            </div>
          )}
        </div>
        {gamification.is_gold_enabled && goldEarned !== null && (
          <div className={styles.gold}>
            <div className={styles.goldItem}>
              <Icon icon="gold" size={18} />
              <Typo element="span" variant="b02" weight="medium">
                {i18n.t('gold-earned', { amount: goldEarned })}
              </Typo>
              {gamification.has_reached_gold_limit && (
                <div className={styles.caution}>
                  <Tooltip content={i18n.t('youve-reached-the-annual-gold-limit')}>
                    <WarningAmberOutlined sx={{ height: 18, width: 18 }} />
                  </Tooltip>
                </div>
              )}
            </div>
          </div>
        )}
        <div className={styles.links}>
          <ButtonGroup>
            {isShareable && credential.allow_sharing && (
              <>
                <Button
                  icon={<LinkedIn />}
                  size="small"
                  text={i18n.t('add-to-linkedin-profile')}
                  to={addToLinkedinURL()}
                  target="_blank"
                  variant="outlined"
                  isExternalLink
                />
                <Menu
                  component={<Button icon={<Share />} size="small" variant="outlined" />}
                  verticalPosition="top"
                >
                  <MenuItem
                    onClick={() =>
                      handleShareClick(
                        `https://www.facebook.com/sharer/sharer.php?u=${credentialViewURL}`,
                        'facebook',
                      )
                    }
                  >
                    <div className={styles.menuItem}>
                      <FacebookRounded sx={{ height: 18, width: 18 }} />
                      <Typo element="span" variant="s02">
                        {i18n.t('share-on-facebook')}
                      </Typo>
                    </div>
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleShareClick(
                        `https://twitter.com/intent/tweet?url=${
                          credentialViewURL
                        }&text=${encodeURIComponent(credential.name)}`,
                        'twitter',
                      )
                    }
                  >
                    <div className={styles.menuItem}>
                      <Twitter sx={{ height: 18, width: 18 }} />
                      <Typo element="span" variant="s02">
                        {i18n.t('share-on-twitter')}
                      </Typo>
                    </div>
                  </MenuItem>
                  <MenuItem
                    onClick={() =>
                      handleShareClick(
                        `http://www.linkedin.com/shareArticle?mini=true&url=${
                          credentialViewURL
                        }&title=${encodeURIComponent(credential.name)}`,
                        'linkedin',
                      )
                    }
                  >
                    <div className={styles.menuItem}>
                      <LinkedIn sx={{ height: 18, width: 18 }} />
                      <Typo element="span" variant="s02">
                        {i18n.t('share-on-linkedin')}
                      </Typo>
                    </div>
                  </MenuItem>
                  <MenuItem onClick={handleCopyClick}>
                    <div className={styles.menuItem}>
                      <Icon icon="copyLink" size={18} />
                      <Typo element="span" variant="s02">
                        {i18n.t('copy-link')}
                      </Typo>
                    </div>
                  </MenuItem>
                </Menu>
              </>
            )}
            <Button icon={<Print />} size="small" variant="outlined" onClick={handlePrintClick} />
          </ButtonGroup>
        </div>
      </div>
    </div>
  );
};

export default React.memo(CredentialModal);
